/**
 * Quantity helpers exported through this folder's `index.ts`: the tier filter re-exported from
 * `@ryerson/frontend.common`, and a quantity-to-pounds conversion used by the inventory check
 * (`components/Shared/CheckInventoryModal/CheckInventoryHelper.tsx`,
 * `CheckInventoryModal/CheckInventoryModalTable/CheckInventoryModalTableQtyAvailableInPound.tsx`).
 */
import { convertQuantityUOM } from "@ryerson/common.conversions";
import { ProductSearchModel, Product, MatchingProduct } from "@ryerson/frontend.common";
import { UOM } from "@ryerson/common.enums";

// getPriceDiscountTier now lives in @ryerson/frontend.common — re-exported here for backward compat
export { getPriceDiscountTier } from "@ryerson/frontend.common";

/**
 * Builds the `convertQuantityUOM` input from a quantity, its units and the product's weights
 * (per piece, per square foot, per foot; 0 when absent). `@ryerson/frontend.common` exports a
 * function of the same name that `PriceBreakdownBox.tsx` uses.
 */
const getConversionUomObject = (
	quantity: number,
	qtyUom: UOM,
	priceUom: UOM,
	productDetail: ProductSearchModel | Product | MatchingProduct | undefined
) => {
	return {
		quantity: quantity.toString(),
		uom: qtyUom,
		pricingUom: priceUom,
		weightPerPiece: Number(productDetail?.weightPerPiece || 0),
		weightPerSquareFoot: Number(productDetail?.weightPerSquareFoot || 0),
		weightPerFoot: Number(productDetail?.weightPerFoot || 0),
	};
};

/**
 * The quantity converted to pounds, as a string with 2 decimals. Returns `undefined` when the
 * quantity is 0 or absent, or when there is no product.
 */
export const getTotalQuantityInLB = (
	qtyUOM: UOM,
	quantity: number | undefined,
	product: ProductSearchModel | Product | MatchingProduct | undefined,
	priceUOM: UOM
) => {
	if (quantity && product) {
		const conversionObject = getConversionUomObject(quantity, qtyUOM, priceUOM, product);
		const quantityInLb = convertQuantityUOM(conversionObject, UOM.LB);
		return Number(quantityInLb).toFixed(2);
	}
};
